import { ReloadOutlined, WarningOutlined } from "@ant-design/icons"
import { Popover } from "antd"
import { CSSProperties } from "react"

import Flex from "#blocks/Flex"
import FormattedTextBlock from "#blocks/FormattedTextBlock"

interface ErrorFallbackLineProps {
  error?: Error
  context?: string
  style?: CSSProperties
  onRetry?: () => void
}

/**
 * Inline/line error fallback for list rows, table cells, text elements.
 * Compact horizontal layout that fits in a single line.
 */
const ErrorFallbackLine = ({
  error,
  context,
  style,
  onRetry,
}: ErrorFallbackLineProps) => {
  // Allow style.color to override text/icon colors
  const textColor = style?.color ?? "#666"

  const errorContent = error?.message ? (
    <Flex col gap={4} style={{ maxWidth: 400 }}>
      <span style={{ fontWeight: 600, fontSize: 12 }}>Error Details</span>
      <FormattedTextBlock
        text={error.message}
        style={{ fontSize: 11, maxHeight: 300, overflowY: "auto" }}
      />
    </Flex>
  ) : (
    "Unknown error"
  )

  return (
    <Flex
      style={{
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        backgroundColor: "rgba(255,77,79,0.15)",
        borderRadius: 4,
        fontSize: 12,
        color: textColor,
        ...style,
      }}
    >
      <Popover content={errorContent} trigger="click" placement="bottom">
        <WarningOutlined
          style={{ color: "#ff4d4f", fontSize: 12, cursor: "pointer" }}
        />
      </Popover>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {context ?? "Error"}
      </span>
      {onRetry && (
        <ReloadOutlined
          onClick={onRetry}
          style={{
            fontSize: 10,
            color: textColor,
            opacity: 0.7,
            cursor: "pointer",
          }}
        />
      )}
    </Flex>
  )
}

export default ErrorFallbackLine
